
// ==========================================
// 5. src/components/sections/ProblemSection.js
// ==========================================
import React from 'react';
import { AlertTriangle, FileText, Zap, X } from 'lucide-react';
import SectionHeader from '../common/SectionHeader';

const ProblemCard = ({ color, icon: Icon, title, description, result }) => {
  const colorMap = {
    red: {
      bg: 'bg-red-50',
      border: 'border-red-100',
      iconBg: 'bg-red-100',
      iconColor: 'text-red-600',
      resultColor: 'text-red-600'
    },
    orange: {
      bg: 'bg-orange-50',
      border: 'border-orange-100',
      iconBg: 'bg-orange-100',
      iconColor: 'text-orange-600',
      resultColor: 'text-orange-600'
    },
    yellow: {
      bg: 'bg-yellow-50',
      border: 'border-yellow-100',
      iconBg: 'bg-yellow-100',
      iconColor: 'text-yellow-600',
      resultColor: 'text-yellow-600'
    }
  };

  const colors = colorMap[color];

  return (
    <div className={`${colors.bg} border ${colors.border} rounded-xl p-6 hover:shadow-lg transition`}>
      <div className={`w-12 h-12 ${colors.iconBg} rounded-lg flex items-center justify-center mb-4`}>
        <Icon className={`w-6 h-6 ${colors.iconColor}`} />
      </div>
      <h3 className="text-xl font-semibold text-gray-900 mb-3">{title}</h3>
      <p className="text-gray-600 mb-3">{description}</p>
      <p className={`text-sm ${colors.resultColor} font-medium`}>
        Result: {result}
      </p>
    </div>
  );
};

const ProblemSection = () => {
  const problems = [
    {
      color: 'red',
      icon: AlertTriangle,
      title: 'External Problems',
      description: (
        <>
          <strong>Disconnected tools everywhere:</strong> Too many portals. Manual dispatching. Tracking gaps. Late status updates. And no way to reconcile payments.
        </>
      ),
      result: 'Missed SLAs & lost business'
    },
    {
      color: 'orange',
      icon: FileText,
      title: 'Internal Frustrations',
      description: (
        <>
          <strong>"We're re-keying the same data"</strong> - Your team spends more time on 
          data entry than strategic work. Morale suffers.
        </>
      ),
      result: 'Burned out teams'
    },
    {
      color: 'yellow',
      icon: Zap,
      title: 'Philosophically Wrong',
      description: (
        <>
          <strong>This isn't how it should be:</strong> Moving freight shouldn’t demand heroic measures.
          Technology should make work easier, not harder.
        </>
      ),
      result: "Can't scale or compete"
    }
  ];

  const stakes = [
    'More missed SLAs',
    'Losing business to tech-forward competitors',
    'Costly compliance failures',
    'Top talent leaves for easier jobs'
  ];

  return (
    <section id="problem" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeader 
          title="The Real Cost of Disconnected Systems"
          subtitle="Every day, your team loses hours to the same preventable problems:"
        />

        {/* Problem Cards */}
        <div className="grid md:grid-cols-3 gap-8">
          {problems.map((problem, index) => (
            <ProblemCard key={index} {...problem} />
          ))}
        </div>

        {/* Stakes Section */}
        <div className="mt-12 bg-gray-900 text-white rounded-2xl p-8 text-center">
          <h3 className="text-2xl font-bold mb-4">If you wait, it only gets worse:</h3>
          <div className="flex flex-wrap justify-center gap-6 text-gray-300">
            {stakes.map((stake, index) => (
              <span key={index} className="flex items-center gap-2">
                <X className="w-5 h-5 text-red-400" />
                {stake}
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProblemSection;